import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { MapPin, Phone, User, Truck, Lock, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useNavigate } from 'react-router-dom';
import { Separator } from '@/components/ui/separator';

interface DeliveryInfoFormProps {
  orderId?: string;
  onComplete?: () => void;
}

export default function DeliveryInfoForm({ orderId, onComplete }: DeliveryInfoFormProps) {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [needsAccount, setNeedsAccount] = useState(false);
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    password: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    postalCode: '',
    country: 'Nigeria',
    deliveryInstructions: ''
  });

  useEffect(() => {
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    
    if (!user && orderId) {
      // Guest checkout - need to create account
      setNeedsAccount(true);
      
      // Get order details to pre-fill email
      const { data: order } = await supabase
        .from('orders')
        .select('payment_reference')
        .eq('id', orderId)
        .single();
      
      if (order?.payment_reference) {
        // Pre-fill email from payment reference (stored during checkout)
        setFormData(prev => ({ ...prev, email: order.payment_reference }));
      }
    }
  };

  const generateTrackingNumber = () => {
    const prefix = 'TRK';
    const timestamp = Date.now().toString(36).toUpperCase();
    const random = Math.random().toString(36).substring(2, 8).toUpperCase();
    return `${prefix}-${timestamp}-${random}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      let userId: string | null = null;

      // Step 1: Create account if needed (guest checkout)
      if (needsAccount) {
        if (!formData.email || !formData.password) {
          toast.error('Email and password are required');
          setIsSubmitting(false);
          return;
        }

        if (formData.password.length < 6) {
          toast.error('Password must be at least 6 characters');
          setIsSubmitting(false);
          return;
        }

        // Create buyer account
        const { data: authData, error: authError } = await supabase.auth.signUp({
          email: formData.email,
          password: formData.password,
          options: {
            emailRedirectTo: `${window.location.origin}/buyer/orders`,
            data: {
              full_name: formData.fullName,
              phone: formData.phone,
              role: 'buyer'
            }
          }
        });

        if (authError) {
          toast.error(authError.message);
          setIsSubmitting(false);
          return;
        }

        userId = authData.user?.id || null;
        toast.success('Account created! Please check your email to verify.');
      } else {
        // Get current user
        const { data: { user } } = await supabase.auth.getUser();
        userId = user?.id || null;
      }

      // Step 2: Generate tracking number
      const trackingNumber = generateTrackingNumber();
      
      // Step 3: Calculate estimated delivery (3-7 business days)
      const estimatedDelivery = new Date();
      estimatedDelivery.setDate(estimatedDelivery.getDate() + Math.floor(Math.random() * 5) + 3);

      // Step 4: Update order with shipping address, buyer ID, and tracking info
      const { error: updateError } = await supabase
        .from('orders')
        .update({
          buyer_id: userId,
          shipping_address: {
            fullName: formData.fullName,
            phone: formData.phone,
            addressLine1: formData.addressLine1,
            addressLine2: formData.addressLine2,
            city: formData.city,
            state: formData.state,
            postalCode: formData.postalCode,
            country: formData.country,
            deliveryInstructions: formData.deliveryInstructions
          },
          tracking_number: trackingNumber,
          estimated_delivery: estimatedDelivery.toISOString(),
          status: 'processing'
        })
        .eq('id', orderId);

      if (updateError) {
        console.error('Update error:', updateError);
        throw updateError;
      }

      toast.success('Delivery information saved successfully!');
      toast.info(`Your tracking number is: ${trackingNumber}`);
      
      if (onComplete) {
        onComplete();
      } else {
        navigate('/buyer/orders');
      }
    } catch (error) {
      console.error('Error saving delivery info:', error);
      toast.error('Failed to save delivery information');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Card className="max-w-2xl mx-auto">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Truck className="h-5 w-5" />
          Delivery Information
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Account Setup (for guest checkout) */}
          {needsAccount && (
            <>
              <div className="space-y-4">
                <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
                  Create Your Account
                </h3>
                <p className="text-sm text-muted-foreground">
                  Create an account to track your order and manage future purchases
                </p>
                
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-2">
                    <Label htmlFor="email">
                      <Mail className="inline h-3 w-3 mr-1" />
                      Email Address *
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      required
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="your@email.com"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor="password">
                      <Lock className="inline h-3 w-3 mr-1" />
                      Password *
                    </Label>
                    <Input
                      id="password"
                      type="password"
                      required
                      minLength={6}
                      value={formData.password}
                      onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                      placeholder="Min. 6 characters"
                    />
                  </div>
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Contact Information */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              Contact Information
            </h3>
            
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="fullName">
                  <User className="inline h-3 w-3 mr-1" />
                  Full Name *
                </Label>
                <Input
                  id="fullName"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                  placeholder="John Doe"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="phone">
                  <Phone className="inline h-3 w-3 mr-1" />
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  placeholder="+234 800 000 0000"
                />
              </div>
            </div>
          </div>

          {/* Delivery Address */}
          <div className="space-y-4">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">
              <MapPin className="inline h-3 w-3 mr-1" />
              Delivery Address
            </h3>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="addressLine1">Address Line 1 *</Label>
                <Input
                  id="addressLine1"
                  required
                  value={formData.addressLine1}
                  onChange={(e) => setFormData({ ...formData, addressLine1: e.target.value })}
                  placeholder="123 Main Street"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="addressLine2">Address Line 2</Label>
                <Input
                  id="addressLine2"
                  value={formData.addressLine2}
                  onChange={(e) => setFormData({ ...formData, addressLine2: e.target.value })}
                  placeholder="Apartment, suite, etc. (optional)"
                />
              </div>
              
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    required
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="Lagos"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    required
                    value={formData.state}
                    onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                    placeholder="Lagos State"
                  />
                </div>
              </div>
              
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="postalCode">Postal Code *</Label>
                  <Input
                    id="postalCode"
                    required
                    value={formData.postalCode}
                    onChange={(e) => setFormData({ ...formData, postalCode: e.target.value })}
                    placeholder="100001"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Input
                    id="country"
                    value={formData.country}
                    disabled
                    className="bg-muted"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Delivery Instructions */}
          <div className="space-y-2">
            <Label htmlFor="deliveryInstructions">
              Delivery Instructions (Optional)
            </Label>
            <Textarea
              id="deliveryInstructions"
              value={formData.deliveryInstructions}
              onChange={(e) => setFormData({ ...formData, deliveryInstructions: e.target.value })}
              placeholder="Any special instructions for delivery..."
              rows={3}
            />
          </div>

          <Button 
            type="submit" 
            variant="gradient" 
            className="w-full"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Processing...' : 'Confirm Delivery Information'}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}